import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, PlugZap, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  getOutboundIntegration,
  listOutboundEvents,
  saveOutboundIntegration,
  testOutboundConnection,
} from "@/lib/rpc/outbound.functions";
import { FeedStatusCard } from "@/components/admin/FeedStatusCard";


export const Route = createFileRoute("/_app/_admin/admin/settings/integrations")({
  component: IntegrationsSettingsPage,
});

function fmt(ts: string | null | undefined) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString();
}

export function IntegrationsSettingsPage() {
  const qc = useQueryClient();
  const getCfg = useServerFn(getOutboundIntegration);
  const listEvents = useServerFn(listOutboundEvents);
  const save = useServerFn(saveOutboundIntegration);
  const test = useServerFn(testOutboundConnection);

  const [url, setUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const cfgQ = useQuery({
    queryKey: ["outbound-integration"],
    queryFn: () => getCfg(),
  });

  const eventsQ = useQuery({
    queryKey: ["outbound-events", from, to],
    queryFn: () => listEvents({ data: { from: from || null, to: to || null } }),
  });

  useEffect(() => {
    if (cfgQ.data) {
      setUrl(cfgQ.data.destination_url);
      setEnabled(cfgQ.data.enabled);
    }
  }, [cfgQ.data]);

  const saveM = useMutation({
    mutationFn: () =>
      save({ data: { destination_url: url, enabled, api_key: apiKey || undefined } }),
    onSuccess: () => {
      setApiKey("");
      toast.success("Integration saved");
      qc.invalidateQueries({ queryKey: ["outbound-integration"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testM = useMutation({
    mutationFn: () => test(),
    onSuccess: (r) => {
      if (r.ok) toast.success(`Connected — HTTP ${r.status} in ${r.duration_ms} ms`);
      else toast.error(r.error ?? `Failed with HTTP ${r.status}`);
      qc.invalidateQueries({ queryKey: ["outbound-integration"] });
      qc.invalidateQueries({ queryKey: ["outbound-events"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cfg = cfgQ.data;
  const statusKind = !cfg || !cfg.key_prefix
    ? "unconfigured"
    : cfg.last_failure_at && (!cfg.last_success_at || cfg.last_failure_at > cfg.last_success_at)
      ? "failing"
      : cfg.last_success_at
        ? "connected"
        : "unconfigured";

  const statusLabel =
    statusKind === "connected" ? "Connected" : statusKind === "failing" ? "Failing" : "Not configured";

  return (
    <div className="space-y-5 px-[22px] py-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">Integrations</h1>
        <p className="text-sm text-muted-foreground">
          Outbound connections from this workspace to external systems.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
        {cfgQ.isLoading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading integration…
          </div>
        ) : cfgQ.isError ? (
          <div className="py-8 text-sm text-destructive">
            Could not load the integration: {(cfgQ.error as Error).message}
          </div>
        ) : (
          <>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <PlugZap className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold">LaunchPanda — Meta Ad Launcher</div>
                  <div className="text-xs text-muted-foreground">
                    One-way push of creative submissions.
                  </div>
                </div>
              </div>
              <span
                className={cn(
                  "rounded-full px-3 py-1 text-[11px] font-medium",
                  statusKind === "connected" && "bg-emerald-500/15 text-emerald-600",
                  statusKind === "failing" && "bg-destructive/15 text-destructive",
                  statusKind === "unconfigured" && "bg-muted text-muted-foreground",
                )}
              >
                {statusLabel}
              </span>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="dest">Destination URL</Label>
                <Input
                  id="dest"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://launchpanda.lovable.app"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="key">API key (write-only)</Label>
                <Input
                  id="key"
                  type="password"
                  value={apiKey}
                  autoComplete="new-password"
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={cfg?.key_prefix ?? "lp_live_…"}
                />
                <p className="text-[11px] text-muted-foreground">
                  Stored encrypted. Currently saved: {cfg?.key_prefix ?? "none"}
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <Switch id="enabled" checked={enabled} onCheckedChange={setEnabled} />
              <Label htmlFor="enabled" className="text-sm">
                Enabled
              </Label>
            </div>

            <div className="mt-5 grid gap-3 rounded-lg bg-muted/40 p-3 text-xs sm:grid-cols-3">
              <div>
                <div className="text-muted-foreground">Last successful send</div>
                <div className="font-medium">{fmt(cfg?.last_success_at)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Last failure</div>
                <div className="font-medium">{fmt(cfg?.last_failure_at)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Last error</div>
                <div className="font-medium break-words">{cfg?.last_error ?? "—"}</div>
              </div>
            </div>

            <div className="mt-5 flex gap-2">
              <Button onClick={() => saveM.mutate()} disabled={saveM.isPending || !url}>
                {saveM.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => testM.mutate()}
                disabled={testM.isPending || !cfg}
              >
                {testM.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Test connection
              </Button>
            </div>
          </>
        )}
      </div>

      <FeedStatusCard />

      <div className="rounded-xl border border-border bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">

          <div className="text-sm font-semibold">Outbound events</div>
          <div className="flex items-center gap-2 text-xs">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-8 w-[150px]"
            />
            <span className="text-muted-foreground">to</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 w-[150px]"
            />
          </div>
        </div>

        {eventsQ.isLoading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
          </div>
        ) : eventsQ.isError ? (
          <div className="p-6 text-sm text-destructive">
            Could not load events: {(eventsQ.error as Error).message}
          </div>
        ) : (eventsQ.data ?? []).length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            No outbound events yet. Use “Test connection” to create the first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-4 py-2 font-medium">Time</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Duration</th>
                  <th className="px-4 py-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {(eventsQ.data ?? []).map((ev) => (
                  <tr key={ev.id} className="border-b border-border/60 last:border-0">
                    <td className="whitespace-nowrap px-4 py-2">{fmt(ev.occurred_at)}</td>
                    <td className="px-4 py-2">{ev.event_type}</td>
                    <td className="px-4 py-2">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[11px] font-medium",
                          ev.ok
                            ? "bg-emerald-500/15 text-emerald-600"
                            : "bg-destructive/15 text-destructive",
                        )}
                      >
                        {ev.http_status ?? (ev.ok ? "OK" : "ERR")}
                      </span>
                    </td>
                    <td className="px-4 py-2">{ev.duration_ms != null ? `${ev.duration_ms} ms` : "—"}</td>
                    <td className="max-w-[380px] truncate px-4 py-2 text-muted-foreground">
                      {ev.error ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
